// @flow

import { action, computed, observable } from "mobx";

import TimelineItemStore from "app/stores/TimelineItemStore";
import UserStore from "app/stores/UserStore";
import VideoStore from "app/stores/VideoStore";
import VideoPlayerStore from "app/stores/VideoPlayerStore";

import TimelineItem from "app/models/TimelineItem";
import TextFormatter from "../../util/TextFormatter";

export type WatchTourItem = {
    name: string,
    referenceClass: string,
    title: string,
    text: string | Function,
    position: string,
    callback?: Function
};

const timelineItemComment =
    "This is so cool! Tip: for images, you can drag and drop here, or just give a URL. 😀";

export default class WatchTourStack {
    items: WatchTourItem[] = [
        this.buildAddAnnotation(),
        this.buildLocationAndDuration(),
        this.buildAddingAnAnnotation(),
        this.buildFinal()
    ];

    @observable selectedIndex: number = -1;

    buildAddAnnotation(): WatchTourItem {
        return {
            name: "addAnnotation",
            referenceClass: "VideoProgress__progress",
            title: "Add a story",
            text: "Let's make a quick one, so you can see how easy it is.",
            position: "right",
            callback: () => {
                const video = VideoStore.video;
                const startTime = video.duration <=10 ? 1 : Math.floor(video.duration / 10);
                const stopTime = Math.max(startTime * 2, startTime + 5);

                const item = new TimelineItem();
                item.start_time = startTime;
                item.stop_time = stopTime;
                item.comment = timelineItemComment;

                TimelineItemStore.startWizard(item.start_time, item);
            }
        };
    }

    buildLocationAndDuration(): WatchTourItem {
        return {
            name: "locationAndDuration",
            referenceClass: "VideoProgress__progress",
            title: "Location and duration",
            position: "right",
            text: () => {
                const item = TimelineItemStore.selectedItem;
                if (!item) {
                    throw new Error("Item not selected in tour");
                }

                const startTime = TextFormatter.seconds(item.start_time);
                const stopTime = TextFormatter.seconds(item.stop_time);
                return `In this example the story will pop at ${startTime} and close at ${stopTime}`;
            }
        };
    }

    buildAddingAnAnnotation(): WatchTourItem {
        return {
            name: "addingAnnotation",
            referenceClass: "TimelineCard__type",
            title: "Adding your story",
            text:
                "This is your space. Add maps, gifs, videos, photos, links, whatever you want. I will put a short sentence so you can see how it looks, ok?",
            position: "bottom",
            callback: () => {
                TimelineItemStore.suppressNextCardCreationMessage = true;
                TimelineItemStore.save(timelineItemComment);
            }
        };
    }

    buildFinal(): WatchTourItem {
        let name = "";
        if (UserStore.user) {
            name = " " + UserStore.user.first_name;
        }

        return {
            name: "final",
            referenceClass: "TimelineCard__type",
            title: "Aaaand... It's alive! \\o/",
            text: `Here you have your awesome story${name}. Add other stories to enhance this video.`,
            position: "bottom",
            callback: () => {
                VideoPlayerStore.setWatchTour(false);
                TimelineItemStore.remove();
            }
        };
    }

    @action
    start() {
        this.selectedIndex = 0;
    }

    @action.bound
    moveNext() {
        if (this.item && this.item.callback) {
            this.item.callback();
        }

        if (this.hasNext) {
            this.selectedIndex++;
        } else {
            this.selectedIndex = -1;
        }
    }

    @computed
    get item(): ?WatchTourItem {
        const index = this.selectedIndex;
        if (index === -1) return null;

        return this.items[index];
    }

    @computed
    get hasNext(): boolean {
        return this.selectedIndex < this.items.length - 1;
    }
}



// WEBPACK FOOTER //
// ./src/app/components/WatchTour/WatchTourStack.js